import React, { memo, RefObject, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ConnectorsProviderContext } from 'src/presentation/components/pages/sidekick/threads/components/Connectors/ConnectorsProvider';
import SvgLine from 'src/presentation/components/pages/sidekick/threads/components/ParentsPanel/SvgLine';
import {
  selectCurrentModule,
  selectCurrentModulesState,
  selectCurrentThread,
  selectLines,
} from 'src/presentation/components/pages/sidekick/threads/actions/threadsUiSlice';
import { FixedContainerRef } from '../../SidekickThreadEditor';

type RelationLinesProps = {
  modulesContainerRef: RefObject<FixedContainerRef>;
  scrollableRef: RefObject<HTMLDivElement>;
};

const RelationLines = ({ modulesContainerRef, scrollableRef }: RelationLinesProps) => {
  const { elementsRef, name: connectorName } = useContext(ConnectorsProviderContext);

  const [isReady, setIsReady] = React.useState(false);
  const [initTimeout, setInitTimeout] = React.useState(500);
  const linesMap = useSelector(selectLines);

  const lines = useMemo(() => linesMap[connectorName] || {}, [connectorName, linesMap]);

  const currentThread = useSelector(selectCurrentThread);
  const currentModule = useSelector(selectCurrentModule);
  const modulesStates = useSelector(selectCurrentModulesState);

  useEffect(() => {
    if (!lines || !currentThread || !modulesStates) {
      return;
    }
    setIsReady(false);
  }, [lines, setIsReady, currentThread, modulesStates]);

  useEffect(() => {
    if (!elementsRef?.current || !lines || !currentThread || isReady) {
      return;
    }

    setTimeout(() => {
      setIsReady(true);
      setInitTimeout(0);
    }, initTimeout);
  }, [setIsReady, elementsRef, lines, currentThread, isReady, setInitTimeout, initTimeout]);

  const drawLines = useMemo(() => {
    if (
      !elementsRef.current ||
      !isReady ||
      !modulesContainerRef?.current ||
      !modulesContainerRef?.current?.elementRef?.current
    ) {
      return [];
    }

    const elements = elementsRef.current;

    const shiftX = modulesContainerRef.current.elementRef.current.getClientRects()[0].x;
    const shiftY = modulesContainerRef.current.elementRef.current.getClientRects()[0].y;

    return Object.keys(lines).map(key => {
      let src = elements[lines[key].source.id];
      const dsc = elements[lines[key].destination.id];

      // line should have different style if module is not selected....
      let hideLines = false;

      if (!src?.element || !dsc?.element) {
        return null;
      }

      if (src.type !== 'THREAD') {
        hideLines = !(
          (src.type === 'MODULE' || src.type === 'MODIFIER') &&
          src.meta &&
          src.meta.moduleId === currentModule.id
        );

        if (!modulesStates[src.meta!.moduleId]?.isOpen) {
          const key = Object.keys(elements).find(
            key => elements[key].type === 'MODULE' && elements[key].id === src.meta!.moduleId
          );
          if (key) {
            src = elements[key];
          }
        }
      }

      const srcRect = src.element.getClientRects()[0];
      const dscRect = dsc.element.getClientRects()[0];

      if (!srcRect) {
        return null;
      }

      return (
        <SvgLine
          from={{ connectionPoint: src.connectedPointPosition, rect: srcRect }}
          hideLines={hideLines}
          key={key}
          shiftX={shiftX}
          shiftY={shiftY}
          to={{ connectionPoint: dsc.connectedPointPosition, rect: dscRect }}
          scrollableRef={scrollableRef}
        />
      );
    });
  }, [lines, elementsRef, isReady, modulesContainerRef, scrollableRef, currentModule, modulesStates]);

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      {drawLines}
    </svg>
  );
};

export default memo(RelationLines);
